import { observer } from "mobx-react";
import { FC } from "react";
import { ArticleSelectorQuestion } from "../common/commonComponents";
import {
  AddArticleModel,
  articleChangeCaptions,
  ArticleChangeDialogState,
  ChangeArticlesOfAssociationForm,
  ChangeArticlesOfAssociationModel,
  EditArticleModel,
  RemoveArticleModel,
} from "./changeArticlesOfAssociationDomain";
import { Box, Grid, GridItem, VBox } from "../../components-v2/Box";
import { DropdownMenu } from "../../components-v2/Dropdown";
import { ArticlesOfAssociationChangeMode } from "../common/commonDomain";
import { Dialog } from "../../components-v2/Dialog";
import { MSelect } from "../../components-v2/Select";
import { MTextInput } from "../../components-v2/TextInput";
import { MarkdownEditor } from "../../text/MarkdownEditor";
import { IconButton, LinkButton } from "../../components-v2/Button";
import { Alert } from "../../components-v2/Alert";
import { ArticleChangeType } from "../../common/common";
import { useLanguage, useTranslation } from "../../stores/RootStoreContext";

export const ChangeArticlesOfAssociationView: FC<{
  form: ChangeArticlesOfAssociationForm;
}> = observer(({ form }) => {
  return <ChangeArticlesOfAssociation model={form.articleChangeModel} />;
});

export const ChangeArticlesOfAssociation: FC<{
  model: ChangeArticlesOfAssociationModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();

  return (
    <VBox gap={"md"}>
      {model.articleChanges.map((articleChange) => (
        <ArticleChangeSummary
          key={articleChange.type + articleChange.paragraph}
          model={model}
          articleChange={articleChange}
        />
      ))}
      <Box gap={"lg"} alignItems={"center"}>
        <DropdownMenu
          button={{
            type: "link",
            label: t("decisions.changeArticlesOfAssociation.addArticleChange"),
            icon: "plus",
            disabled: model.isProjectLocked,
          }}
          items={Object.values(ArticleChangeType).map((type) => ({
            type: "item",
            label: t(articleChangeCaptions[type]),
            onSelect: () => model.showArticleChangeDialog(type),
          }))}
        />
      </Box>
      {model.error && <Alert message={t(model.error)} type={"error"} />}
      <ArticleChangeDialog state={model.dialogState} isLocked={model.isProjectLocked} />
    </VBox>
  );
});

export const ArticleChangeSummary = observer(function ({
  model,
  articleChange,
}: {
  model: ChangeArticlesOfAssociationModel;
  articleChange: ArticlesOfAssociationChangeMode;
}) {
  const { t } = useTranslation();
  const language = useLanguage();

  function renderContent() {
    switch (articleChange.type) {
      case "AddArticle":
        return (
          <ArticleChange
            typeLabel={t("add")}
            title={`§ ${articleChange.paragraph} ${model.getArticleTitleAfterChange(articleChange, language)}`}
            description={model.getArticleTextAfterChange(articleChange, language)}
          />
        );
      case "EditArticle": {
        return (
          <ArticleChange
            typeLabel={t("decisions.changeArticlesOfAssociation.edit")}
            title={`§ ${articleChange.paragraph} ${model.getArticleTitleAfterChange(articleChange, language)}`}
            description={model.getArticleTextAfterChange(articleChange, language)}
          />
        );
      }
      case "RemoveArticle": {
        return (
          <ArticleChange
            typeLabel={t("delete")}
            title={`§ ${articleChange.paragraph} ${model.getArticleTitleBeforeChange(articleChange, language)}`}
            description={model.getArticleTextBeforeChange(articleChange, language)}
          />
        );
      }
    }
  }
  return (
    <Grid alignItems={"center"}>
      <GridItem xs={6}>{renderContent()}</GridItem>
      <GridItem xs={6}>
        <Box justifyContent={"end"} gap={"lg"}>
          {articleChange.type !== ArticleChangeType.RemoveArticle && (
            <LinkButton
              label={t("edit")}
              icon={"edit"}
              onClick={() => model.showArticleChangeDialog(articleChange.type, articleChange)}
            />
          )}
          <IconButton
            icon={"trash"}
            tooltip={t("delete")}
            onClick={() => model.showDeleteArticleDialog(articleChange)}
          />
        </Box>
      </GridItem>
    </Grid>
  );
});
export const ArticleChange = observer(function ({
  typeLabel,
  title,
  description,
}: {
  typeLabel: string;
  title: string;
  description: string;
}) {
  return (
    <VBox gap={false}>
      <Box fg={"subtle"} textSize={"sm"}>
        {typeLabel.toUpperCase()}
      </Box>
      <div className={"tw-overflow-hidden text-ellipsis tw-whitespace-nowrap"}>{title}</div>
      <div className={"text-sm subtle tw-overflow-hidden text-ellipsis tw-whitespace-nowrap"}>
        {description}
      </div>
    </VBox>
  );
});

export const ArticleChangeDialog = observer(function ({
  state,
  isLocked,
}: {
  state: ArticleChangeDialogState;
  isLocked: boolean;
}) {
  switch (state.type) {
    case ArticleChangeType.AddArticle:
      return <AddArticleDialog model={state.model} isLocked={isLocked} />;
    case ArticleChangeType.RemoveArticle:
      return <RemoveArticleDialog model={state.model} isLocked={isLocked} />;
    case ArticleChangeType.EditArticle:
      return <EditArticleDialog model={state.model} isLocked={isLocked} />;
    case "delete":
      return (
        <DeleteDialog
          data={state.data}
          onSubmit={state.onSubmit}
          onCancel={state.onCancel}
          isLocked={isLocked}
        />
      );
    default:
      return null;
  }
});

export const DeleteDialog = observer(
  ({
    data,
    onSubmit,
    onCancel,
    isLocked,
  }: {
    data: ArticlesOfAssociationChangeMode;
    onSubmit: (data: ArticlesOfAssociationChangeMode) => void;
    onCancel: () => void;
    isLocked: boolean;
  }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t("decisions.changeArticlesOfAssociation.deleteArticleChange")}
        submitLabel={t("delete")}
        onSubmit={() => onSubmit(data)}
        isSubmitDisabled={isLocked}
        onCancel={onCancel}
      >
        {t("decisions.changeArticlesOfAssociation.deleteArticleChange.description")}
      </Dialog>
    );
  },
);

export const RemoveArticleDialog = observer(
  ({ model, isLocked }: { model: RemoveArticleModel; isLocked: boolean }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t(articleChangeCaptions[model.type])}
        submitLabel={t("save")}
        onSubmit={model.submit}
        isSubmitDisabled={!model.canSubmit}
        onCancel={model.close}
      >
        <VBox>
          <ArticleSelectorQuestion
            question={t("decisions.changeArticlesOfAssociation.what-paragraph-delete")}
            model={model.paragraph}
            isLocked={isLocked || model.isEdit}
          />
        </VBox>
      </Dialog>
    );
  },
);

export const AddArticleDialog = observer(
  ({ model, isLocked }: { model: AddArticleModel; isLocked: boolean }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t(articleChangeCaptions[model.type])}
        submitLabel={t("save")}
        onSubmit={model.submit}
        isSubmitDisabled={!model.canSubmit}
        onCancel={model.close}
      >
        <VBox gap={"lg"}>
          <Box>
            <div>{t("decisions.changeArticlesOfAssociation.what-paragraph-add")}</div>
            <MSelect model={model.paragraph} disabled={model.isEdit} />
          </Box>
          <Grid gap={"lg"}>
            {model.languages.map((l) => {
              const title = model.titles.find((d) => d.locale === l);
              const text = model.texts.find((d) => d.locale === l);
              return title !== undefined && text !== undefined ? (
                <GridItem xs={model.languages.length === 2 ? 6 : 12} key={title.locale}>
                  <VBox gap={"md"} marginTop={"md"}>
                    <h4>{t(`language.${title.locale}`)}</h4>
                    <Box>
                      <h4>§ {model.paragraph.value}</h4>
                      <MTextInput model={title.model} readOnly={isLocked} />
                    </Box>
                    <MarkdownEditor
                      key={text.locale}
                      value={text.model.inputValue}
                      onChange={text.model.setAndCommitInputValue}
                      readOnly={isLocked}
                    />
                  </VBox>
                </GridItem>
              ) : null;
            })}
          </Grid>
        </VBox>
      </Dialog>
    );
  },
);

export const EditArticleDialog = observer(
  ({ model, isLocked }: { model: EditArticleModel; isLocked: boolean }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t(articleChangeCaptions[model.type])}
        submitLabel={t("save")}
        onSubmit={model.submit}
        isSubmitDisabled={!model.canSubmit}
        onCancel={model.close}
      >
        <VBox gap={"lg"}>
          <ArticleSelectorQuestion
            question={t("decisions.changeArticlesOfAssociation.what-paragraph-change")}
            onChange={model.setParagraphValue}
            model={model.paragraph}
            isLocked={isLocked || model.isEdit}
          />
          <Grid gap={"lg"}>
            {model.paragraph.articles?.languages.map((l) => {
              const title = model.titles.find((d) => d.locale === l);
              const text = model.texts.find((d) => d.locale === l);
              return title !== undefined && text !== undefined ? (
                <GridItem xs={model.languages.length === 2 ? 6 : 12} key={title.locale}>
                  <VBox gap={"md"} marginTop={"md"}>
                    <h4>{t(`language.${title.locale}`)}</h4>
                    <Box>
                      <h4>§ {model.paragraph.selected}</h4>
                      <MTextInput model={title.model} readOnly={isLocked} />
                    </Box>
                    <MarkdownEditor
                      key={text.locale}
                      value={text.model.inputValue}
                      onChange={text.model.setAndCommitInputValue}
                      readOnly={isLocked}
                    />
                  </VBox>
                </GridItem>
              ) : null;
            })}
          </Grid>
        </VBox>
      </Dialog>
    );
  },
);
